import React, { useCallback, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useLocation, useNavigate } from 'react-router-dom';

import { useSelector } from 'react-redux';
import { analyticsService } from '@cobu/analytics';
import { RootState } from '../store';
import { showErrorMessage } from '../utils/messageToast';
import { Strings } from '../constants/Strings';
import {
  useGetNotificationsQuery,
  useMarkAllAsReadMutation,
  useMarkAsReadMutation,
  getNotifications,
} from '@cobu/api-client';
import { Notification, NotificationGroup } from '@cobu/types';
import { useNotificationRouting } from './useNotificationRouting';
import { logger } from '../utils/logger';

const strings = Strings.get();

interface UseNotificationsReturn {
  // Data
  notificationGroups: NotificationGroup[];
  totalNotifications: number;

  // Loading states
  isLoading: boolean;
  isRefreshing: boolean;
  isLoadingMore: boolean;
  error: string | null;

  // Pagination
  hasMoreData: boolean;
  currentPage: number;

  // Actions
  handleRefresh: () => void;
  handleLoadMore: () => void;
  handleMarkAllAsRead: () => Promise<boolean>;
  handleMarkAsRead: (notificationUuid: string) => void;
  handleNotificationPress: (notification: Notification) => void;
  handleSettingsPress: () => void;
}

const PAGINATION_LIMIT = 30;

export function useNotifications(): UseNotificationsReturn {
  const user = useSelector((state: RootState) => state.auth.user);
  const [currentPage, setCurrentPage] = useState(1);
  const [allNotifications, setAllNotifications] = useState<Notification[]>([]);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const navigate = useNavigate();
  const pathname = useLocation().pathname;
  const { navigateFromNotification } = useNotificationRouting();

  const userUuid = user?.uuid;

  // Fetch notifications (only first page via React Query)
  const {
    data: notificationsData,
    isLoading,
    error: queryError,
    refetch,
  } = useGetNotificationsQuery(userUuid!, { page: 1 });

  // Handle new data
  React.useEffect(() => {
    if (!notificationsData) return;

    // Initial load / refresh: replace list with page 1
    setAllNotifications(notificationsData);
    setCurrentPage(1);
    setHasMoreData(notificationsData.length >= PAGINATION_LIMIT);
  }, [notificationsData]);

  // Mutations
  const markAllAsReadMutation = useMarkAllAsReadMutation();
  const markAsReadMutation = useMarkAsReadMutation();

  // Computed values
  const error = queryError?.message || null;
  const totalNotifications = allNotifications.length;

  // Group notifications by time period
  const notificationGroups = useMemo(() => {
    const groups: NotificationGroup[] = [];
    const today: Notification[] = [];
    const yesterday: Notification[] = [];
    const older: Notification[] = [];
    
    allNotifications.forEach(notification => {
      if (!notification.createdAt || !dayjs(notification.createdAt).isValid()) {
        return;
      }

      const notificationDate = dayjs(notification.createdAt);
      const now = dayjs();

      if (notificationDate.isSame(now, 'day')) {
        today.push(notification);
      } else if (notificationDate.isSame(now.subtract(1, 'day'), 'day')) {
        yesterday.push(notification);
      } else {
        older.push(notification);
      }
    });

    if (today.length > 0) {
      groups.push({ title: strings.notifications.groups.today, data: today });
    }
    if (yesterday.length > 0) {
      groups.push({ title: strings.notifications.groups.yesterday, data: yesterday });
    }
    if (older.length > 0) {
      groups.push({ title: strings.notifications.groups.older, data: older });
    }

    return groups;
  }, [allNotifications]);

  // Handle refresh
  const handleRefresh = useCallback(async () => {
    analyticsService.trackEvent('NOTIFICATION_CENTER_REFRESHED');
    setIsRefreshing(true);
    try {
      setCurrentPage(1);
      setHasMoreData(true);
      await refetch();
    } catch (error) {
      logger.error('Error refreshing notifications:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [refetch]);

  // Handle load more
  const handleLoadMore = useCallback(async () => {
    if (!userUuid || !hasMoreData || isLoadingMore) return;

    analyticsService.trackEvent('NOTIFICATION_CENTER_LOAD_MORE', {
      currentPage,
      totalNotifications,
    });

    setIsLoadingMore(true);
    try {
      const nextPage = currentPage + 1;
      const more = await getNotifications(userUuid, { page: nextPage });

      if (!more || more.length === 0) {
        setHasMoreData(false);
        return;
      }

      setAllNotifications(prev => {
        const seen = new Set(prev.map(n => n.uuid));
        const merged = [...prev];
        more.forEach(n => {
          if (!seen.has(n.uuid)) {
            seen.add(n.uuid);
            merged.push(n);
          }
        });
        return merged;
      });

      setCurrentPage(nextPage);

      if (more.length < PAGINATION_LIMIT) {
        setHasMoreData(false);
      }
    } catch (error) {
      logger.error('Error loading more notifications:', error);
      showErrorMessage(strings.common.somethingWentWrong);
    } finally {
      setIsLoadingMore(false);
    }
  }, [userUuid, hasMoreData, isLoadingMore, currentPage, totalNotifications]);

  // Handle mark all as read
  const handleMarkAllAsRead = useCallback(async (): Promise<boolean> => {
    if (!userUuid) return false;
    try {
      await markAllAsReadMutation.mutateAsync(userUuid);
      // Reset pagination and refresh to show updated state
      setCurrentPage(1);
      setHasMoreData(true);
      await refetch();
      return true;
    } catch (e) {
      // Silent fail for now
      return false;
    }
  }, [userUuid, markAllAsReadMutation, refetch]);

  // Handle mark single notification as read
  const handleMarkAsRead = useCallback(
    async (notificationUuid: string) => {
      try {
        analyticsService.trackEvent('NOTIFICATION_MARK_READ', {
          notificationUuid,
        });
        
        await markAsReadMutation.mutateAsync(notificationUuid);
      } catch (error) {
        showErrorMessage(strings.notifications.messages.markSingleFailed);
      }
    },
    [markAsReadMutation]
  );

  // Handle notification press
  const handleNotificationPress = useCallback(
    (notification: Notification) => {
      analyticsService.trackEvent('NOTIFICATION_PRESSED', {
        notificationUuid: notification.uuid,
        notificationType: (notification as any)?.type,
      });

      if (!notification.isRead) {
        handleMarkAsRead(notification.uuid);
      }

      navigateFromNotification(notification);
    },
    [handleMarkAsRead, navigate]
  );

  // Handle settings press
  const handleSettingsPress = useCallback(() => {
    analyticsService.trackEvent('NOTIFICATION_SETTINGS_OPENED');
    // No settings screen available on web; noop
    return;
  }, [navigate]);

  return {
    // Data
    notificationGroups,
    totalNotifications,

    // Loading states
    isLoading,
    isRefreshing,
    isLoadingMore,
    error,

    // Pagination
    hasMoreData,
    currentPage,

    // Actions
    handleRefresh,
    handleLoadMore,
    handleMarkAllAsRead,
    handleMarkAsRead,
    handleNotificationPress,
    handleSettingsPress,
  };
} 